import { inject, Injectable } from '@angular/core';
import {
  CheckoutValidator,
  PaymentMode,
  ReceiptTypeCodes,
  SelectedPaymentMode,
  ValidationErrorKeys,
  ValidationErrors,
} from '@checkout-v2/const';
import { PaymentModesFacade } from '@checkout-v2/data-access';
import { StateUtils } from '@spartacus/core';
import { CurrentProfileService } from '@user/feature';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { PickupService } from './pickup.service';
import { ReceiptService } from './receipt.service';
import { TimeSlotsService } from './time-slots.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentModesService extends CheckoutValidator {
  private readonly paymentModesFacade = inject(PaymentModesFacade);
  private readonly timeSlotsService = inject(TimeSlotsService);
  private readonly pickupService = inject(PickupService);
  private readonly receiptService = inject(ReceiptService);
  private readonly currentProfileService = inject(CurrentProfileService);

  getListState(): Observable<StateUtils.LoaderState<PaymentMode[]>> {
    return this.paymentModesFacade.getListState();
  }

  getList(): Observable<PaymentMode[]> {
    return this.paymentModesFacade.getList();
  }

  getListLoading(): Observable<boolean> {
    return this.paymentModesFacade.getListLoading();
  }

  getListError(): Observable<boolean> {
    return this.paymentModesFacade.getListError();
  }

  getListSuccess(): Observable<boolean> {
    return this.paymentModesFacade.getListSuccess();
  }

  initCardBindingOnExternalAddress(): void {
    this.paymentModesFacade.initCardBindingOnExternalAddress();
  }

  setActivePaymentMode(paymentMode: SelectedPaymentMode | undefined): void {
    this.paymentModesFacade.setActivePaymentMode(paymentMode);
  }

  getActivePaymentMode(): Observable<SelectedPaymentMode | undefined> {
    return this.paymentModesFacade.getActivePaymentMode();
  }

  getActivePaymentModeError(): Observable<boolean> {
    return this.paymentModesFacade.getActivePaymentModeError();
  }

  isEmptyOrHasError(): Observable<boolean> {
    return combineLatest({
      isTimeSlotsOrPickupEmptyOrHasError: this.receiptService.getActiveReceiptTypeId().pipe(
        switchMap((type) => {
          if (type === ReceiptTypeCodes.Delivery) {
            return this.timeSlotsService.isEmptyOrHasError();
          }

          if (type === ReceiptTypeCodes.Pickup) {
            return this.pickupService.isEmptyOrHasError();
          }

          return of(false);
        }),
      ),
      state: this.getListState(),
      activePaymentModeError: this.getActivePaymentModeError(),
    }).pipe(
      map(({ isTimeSlotsOrPickupEmptyOrHasError, state, activePaymentModeError }) => {
        return (
          isTimeSlotsOrPickupEmptyOrHasError ||
          !!state.error ||
          !!activePaymentModeError ||
          (!!state.value && state.value.length === 0)
        );
      }),
    );
  }

  getValidationErrors(): Observable<ValidationErrors> {
    return combineLatest({
      isLegalProfile: this.currentProfileService.getLegalEntityStatus(),
      isEmptyOrHasError: this.isEmptyOrHasError(),
      activePaymentMode: this.getActivePaymentMode(),
    }).pipe(
      map(({ isLegalProfile, isEmptyOrHasError, activePaymentMode }) => {
        const validationErrors: ValidationErrors = {};

        if (!isLegalProfile && !isEmptyOrHasError && !activePaymentMode) {
          validationErrors[ValidationErrorKeys.PAYMENT_REQUIRED] = 'выберите способ оплаты';
        }

        return validationErrors;
      }),
    );
  }
}
